import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Button } from '@zond/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetProjects } from '../projects.queries';
import { DataTablePagination } from './components/data-table-pagination';
import DataTableToolbar from './components/data-table-toolbar';
import { columns } from './components/projects-columns';
import { ProjectsDialogs } from './components/projects-dialogs';
import { ProjectsProvider, useProjects } from './context';

function ProjectsContent() {
  const { t } = useTranslation('projects');
  const { data: projects, isLoading } = useGetProjects();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { setOpen } = useProjects();

  const table = useReactTable({
    data: projects || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
      columnFilters: [],
    },
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('list.title')}</h1>
          <p className="text-muted-foreground">{t('list.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setOpen('create')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('actions.createProject')}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <DataTableToolbar table={table} />

        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:hidden first:pl-4 last:pr-4"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-t hover:bg-muted/50">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="p-2 [&:has([role=checkbox])]:hidden first:pl-4 last:pr-4">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="h-24 text-center py-6">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="text-center">
                        <h3 className="text-lg font-semibold">{t('list.empty.title')}</h3>
                        <p className="text-sm text-muted-foreground">{t('list.empty.description')}</p>
                      </div>
                      <Button onClick={() => setOpen('create')}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('actions.createProject')}
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination table={table} />
        <ProjectsDialogs />
      </div>
    </div>
  );
}

export function Projects() {
  return (
    <ProjectsProvider>
      <ProjectsContent />
    </ProjectsProvider>
  );
}
